<script lang="ts">
import type { SwitchRootProps } from 'reka-ui'
import type { Component } from 'vue'
import type { Color } from '../styles/colors'

export interface SwitchProps extends Pick<SwitchRootProps, 'disabled' | 'id' | 'name' | 'required' | 'value' | 'defaultValue'> {
  as?: string | Component
  color?: Color
  label?: string
  description?: string
  indicatorPosition?: 'start' | 'end'
  size?: 'sm' | 'md'
  class?: string | object
}

export interface SwitchEmits {
  change: [payload: Event]
}

export interface SwitchSlots {
  label?: (props: { label?: string }) => any
  description?: (props: { description?: string }) => any
}

const ui = {
  root: 'relative flex items-start',
  base: 'w-9 inline-flex items-center shrink-0 rounded-full border-2 border-transparent transition-[background] duration-200',
  container: 'h-5.5 flex items-center',
  thumb: 'bg-white size-4 data-[state=checked]:translate-x-4 group pointer-events-none rounded-full bg-default shadow-lg ring-0 transition-transform duration-200 data-[state=unchecked]:translate-x-0 flex items-center justify-center',
  wrapper: '',
  label: 'block font-semibold text-sm mt-0.75',
  description: 'text-muted text-sm text-secondary-800 mt-0.5',
  size: {
    sm: {
      base: 'w-7',
      container: 'h-4.5',
      thumb: 'size-3 data-[state=checked]:translate-x-3',
      label: 'text-xs',
      description: 'text-xs',
    },
    md: {
      base: 'w-9',
      container: 'h-5.5',
      thumb: 'size-4 data-[state=checked]:translate-x-4',
      label: 'text-sm',
      description: 'text-sm',
    },
  },
  required: {
    label: 'after:content-[\'*\'] after:ms-0.5 after:text-red-600',
  },
  disabled: {
    base: 'cursor-not-allowed opacity-30',
    label: 'cursor-not-allowed opacity-30',
    description: 'cursor-not-allowed opacity-30',
  },
  indicator: {
    start: {
      root: 'flex-row',
      wrapper: 'ms-2',
    },
    end: {
      root: 'flex-row-reverse justify-between',
      wrapper: 'me-2',
    },
  },
  unchecked: 'bg-secondary-400',
  checked: {
    default: 'bg-{color}-500',
    secondary: 'bg-secondary-900',
  },
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core'
import { Label, Primitive, SwitchRoot, SwitchThumb, useForwardProps } from 'reka-ui'
import { computed, useId } from 'vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<SwitchProps>(), {
  size: 'md',
})
const emits = defineEmits<SwitchEmits>()
const slots = defineSlots<SwitchSlots>()
const modelValue = defineModel<boolean>({ default: undefined })

const rootProps = useForwardProps(reactivePick(props, 'required', 'value', 'defaultValue'))

const id = useId()

function onUpdate(value: any) {
  // @ts-expect-error - 'target' does not exist in type 'EventInit'
  const event = new Event('change', { target: { value } })
  emits('change', event)
}

const indicatorPosition = computed(() => props.indicatorPosition || 'start')
const color = computed(() => props.color || 'primary')
const baseClass = computed(() => [
  'inline-flex items-center shrink-0 rounded-full border-2 border-transparent transition-[background] duration-200',
  ui.size[props.size].base,
  props.disabled && ui.disabled.base,
  modelValue.value ? ui.checked[color.value === 'secondary' ? 'secondary' : 'default'].replaceAll('{color}', color.value) : ui.unchecked,
])
const containerClass = computed(() => [
  'flex items-center',
  ui.size[props.size].container,
])
const thumbClass = computed(() => [
  'bg-white group pointer-events-none rounded-full bg-default shadow-lg ring-0 transition-transform duration-200 data-[state=unchecked]:translate-x-0 flex items-center justify-center',
  ui.size[props.size].thumb,
])
const labelClass = computed(() => [
  'block font-semibold mt-0.75',
  ui.size[props.size].label,
  props.required && ui.required.label,
])
const descriptionClass = computed(() => [
  'text-muted text-secondary-800 mt-0.5',
  ui.size[props.size].description,
])
const rootClass = computed(() => [ui.root, ui.indicator[indicatorPosition.value].root])
const wrapperClass = computed(() => [ui.wrapper, ui.indicator[indicatorPosition.value].wrapper])
</script>

<template>
  <Primitive :as="as" :class="rootClass">
    <div :class="containerClass">
      <SwitchRoot
        :id
        v-bind="{ ...rootProps, ...$attrs }"
        v-model="modelValue"
        :name
        :disabled
        :class="baseClass"
        @update:model-value="onUpdate"
      >
        <SwitchThumb :class="thumbClass" />
      </SwitchRoot>
    </div>
    <div v-if="(label || !!slots.label) || (description || !!slots.description)" :class="wrapperClass">
      <Label v-if="label || !!slots.label" :for="id" :class="labelClass">
        <slot name="label" :label>
          {{ label }}
        </slot>
      </Label>
      <p v-if="description || !!slots.description" :class="descriptionClass">
        <slot name="description" :description>
          {{ description }}
        </slot>
      </p>
    </div>
  </Primitive>
</template>
